/**
 * Facebook-like My Network page: contacts, presence, profile, and messaging.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Box, Divider, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";

import { VAvatar, VButton, VInput, VText } from "form";
import { useAsterController } from "context";
import { actionNetworkList } from "context/action";
import { asArray, asNumber, asObject, asString } from "utils/safeData";
import NetworkThread from "./network.thread";

/**
 * Unread count badge on a contact avatar, matching inbox.
 *
 * @param {number} count Unread messages.
 * @returns {JSX.Element|null}
 */
const UnreadBadge = ({ count }) =>
  count > 0 ? (
    <VText
      className="absolute -top-1 -right-1 px-1 h-4 bg-primary rounded-full flex justify-center items-center text-xs text-white z-10"
      div
      style={{ minWidth: "16px" }}
    >
      {count}
    </VText>
  ) : null;

/**
 * Two-column network directory with an optional live chat pane.
 */
const Network = () => {
  const { t } = useTranslation("common");
  const [searchParams, setSearchParams] = useSearchParams();
  const [controller, dispatch] = useAsterController();
  const { networkContacts, loadedNetworkList, networkUnread } = controller;

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [contentHeight, setContentHeight] = useState(500);
  const peerParam = searchParams.get("peer");

  useEffect(() => {
    if (!controller.sessionHydrated) return;
    actionNetworkList(dispatch);
  }, [controller.sessionHydrated, dispatch]);

  useEffect(() => {
    const titleHeight = asNumber(document.getElementById("v-network-title")?.offsetHeight);
    const totalHeight = asNumber(document.body?.offsetHeight);
    setContentHeight(Math.max(asNumber(totalHeight - titleHeight - 77 - 48 - 90), 0));
  }, []);

  const contacts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return asArray(networkContacts).filter((row) => {
      const item = asObject(row);
      const name = `${asString(item.fullname)} ${asString(item.username)}`.toLowerCase();
      if (term && !name.includes(term)) return false;
      if (filter === "online") return Boolean(item.online);
      return true;
    });
  }, [networkContacts, search, filter]);

  const selected = useMemo(() => {
    if (!peerParam) return null;
    return asArray(networkContacts).find((row) => String(asObject(row).user_id) === String(peerParam)) || null;
  }, [networkContacts, peerParam]);

  /**
   * Opens a contact in the thread pane.
   *
   * @param {number|string} userId Contact id.
   * @returns {void}
   */
  const openPeer = (userId) => setSearchParams({ peer: String(userId) });

  return (
    <Box className="flex-1 md:p-6">
      <Box className="flex flex-col md:rounded-xl h-full v-bg-body">
        <Box className="px-6 py-8" id="v-network-title">
          <Grid container spacing={2}>
            <Grid item sm={12} className="flex">
              <VText color="secondary" className="text-2xl">
                {t("My Network")}
              </VText>
            </Grid>
          </Grid>
        </Box>
        <Box className="flex-1 flex flex-col md:flex-row h-auto md:h-full">
          <Box
            className="w-full md:w-[348px] md:min-w-[348px] border-0 border-r border-t border-solid border-gray-300 dark:border-gray-500 flex flex-col"
            style={{ height: contentHeight + 88 }}
          >
            <Box className="p-4">
              <VInput
                startIcon="search"
                placeholder={t("Search people")}
                size="small"
                className="v-input-noborder w-full"
                noBorder
                value={search}
                setValue={setSearch}
              />
              <Box className="flex gap-2 mt-3">
                <VButton
                  variant={filter === "all" ? "contained" : "outlined"}
                  color="primary"
                  size="small"
                  onClick={() => setFilter("all")}
                >
                  {t("All")}
                </VButton>
                <VButton
                  variant={filter === "online" ? "contained" : "outlined"}
                  color="primary"
                  size="small"
                  onClick={() => setFilter("online")}
                >
                  {t("Online")}
                </VButton>
              </Box>
            </Box>
            <Divider className="bg-gray-200 dark:bg-gray-500" />
            <Box className="flex-1 overflow-y-auto v-light-scrollbar">
              {loadedNetworkList ? (
                contacts.length ? (
                  contacts.map((raw) => {
                    const item = asObject(raw);
                    const name = asString(item.fullname) || asString(item.username) || "-";
                    const active = String(item.user_id) === String(peerParam);
                    const unread = Number(asObject(networkUnread)[String(item.user_id)]) || 0;
                    return (
                      <Box
                        key={item.user_id}
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer ${active ? "bg-gray-50 dark:bg-gray-700 border-0 border-l-4 border-solid border-yellow-400" : "hover:bg-gray-50 dark:hover:bg-gray-700"}`}
                        onClick={() => openPeer(item.user_id)}
                      >
                        <Box className="relative">
                          <VAvatar size="sm" bgColor="light" src={asString(item.avatar)} />
                          <UnreadBadge count={unread} />
                          <Box className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-gray-800 ${item.online ? "bg-green-500" : "bg-gray-300"}`} />
                        </Box>
                        <Box className="min-w-0 flex-1">
                          <VText className="text-sm font-bold text-limit-1">{name}</VText>
                          <VText color="secondary" className="text-xs">
                            {t(asString(item.permission, "USER"))} · {item.online ? t("Online") : t("Offline")}
                          </VText>
                        </Box>
                        <VButton
                          variant="text"
                          color="primary"
                          size="small"
                          className="normal-case"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPeer(item.user_id);
                          }}
                        >
                          {t("Message")}
                        </VButton>
                      </Box>
                    );
                  })
                ) : (
                  <VText color="secondary" className="text-sm p-4">
                    {t("No contacts yet")}
                  </VText>
                )
              ) : (
                <Box className="p-4">
                  <Skeleton className="h-12 mt-1" count={5} />
                </Box>
              )}
            </Box>
          </Box>
          {selected ? (
            <NetworkThread peer={selected} height={contentHeight} />
          ) : (
            <Box className="flex-1 flex items-center justify-center p-8 border-0 border-t border-solid border-gray-300 dark:border-gray-500" style={{ height: contentHeight + 88 }}>
              <VText color="secondary">{t("Select a contact to message")}</VText>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Network;
